import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Truck } from "lucide-react";

import { api, type TransactionList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { supplyCategoryIdSchema, type SupplyCategoryId } from "@/domain/suppliers";
import { FinancePresentation } from "@/presentation/finance";

export function SuppliersView({ data }: { data: TransactionList }) {
	const queryClient = useQueryClient();
	const supplierCategoryMutation = useMutation({
		mutationFn: (input: { supplierId: string; categoryId: SupplyCategoryId }) =>
			api.suppliers.setCategory(input),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["transactions", "current-month"] });
		},
	});
	const sugarSuppliers = data.suppliers.filter((supplier) => supplier.category.id === "sugar");
	const freightSuppliers = data.suppliers.filter(
		(supplier) => supplier.category.id === "inbound_freight",
	);
	const packagingSuppliers = data.suppliers.filter(
		(supplier) => supplier.category.id === "packaging",
	);
	const suppliersWithBankIdentity = data.suppliers.filter(
		(supplier) => supplier.bankAliases.length > 0,
	).length;

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
				<MetricCard
					label="Fornecedores cadastrados"
					value={String(data.suppliers.length)}
					detail="cadastro comum do Financeiro"
				/>
				<MetricCard
					label="Fornecedores de açúcar"
					value={String(sugarSuppliers.length)}
					detail="usinas, trading e empacotadora"
				/>
				<MetricCard
					label="Transportadoras"
					value={String(freightSuppliers.length)}
					detail="frete de entrada do açúcar"
				/>
				<MetricCard
					label="Fornecedores de embalagens"
					value={String(packagingSuppliers.length)}
					detail="matéria-prima da produção"
				/>
				<MetricCard
					label="Identidade bancária"
					value={`${suppliersWithBankIdentity}/${data.suppliers.length}`}
					detail="reconhecidos no extrato"
					tone="success"
				/>
			</section>

			<section className="overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-[0_20px_60px_-35px_rgba(28,25,23,0.35)]">
				<div className="border-b border-stone-200 px-5 py-4 sm:px-6">
					<h2 className="font-semibold text-stone-950">Cadastro de fornecedores</h2>
					<p className="mt-1 max-w-3xl text-xs leading-5 text-stone-500">
						O nome recebido do banco reconhece o fornecedor. O fornecimento selecionado define a
						regra para novos pagamentos; correções humanas continuam preservadas no histórico.
					</p>
					{supplierCategoryMutation.error && (
						<p className="mt-2 text-xs text-red-700">{supplierCategoryMutation.error.message}</p>
					)}
				</div>
				<div className="grid gap-px bg-stone-200 lg:grid-cols-2">
					{data.suppliers.map((supplier) => (
						<div key={supplier.id} className="bg-white p-5 sm:p-6">
							<div className="flex items-start gap-3">
								<div
									className={`grid size-10 shrink-0 place-items-center rounded-xl ${supplier.kind === "CARRIER" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-700"}`}
								>
									{supplier.kind === "CARRIER" ? (
										<Truck className="size-4" />
									) : (
										<Building2 className="size-4" />
									)}
								</div>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-2">
										<h3 className="font-semibold text-stone-950">{supplier.name}</h3>
										<span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
											{FinancePresentation.supplierKind(supplier.kind)}
										</span>
									</div>
									{supplier.legalName && (
										<p className="mt-1 truncate text-xs text-stone-500">{supplier.legalName}</p>
									)}
									<p className="mt-1 text-xs text-stone-500">
										{supplier.taxId
											? FinancePresentation.taxId(supplier.taxId)
											: "CNPJ ainda não confirmado"}
									</p>
								</div>
							</div>

							<div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,1fr)_220px] sm:items-end">
								<div className="min-w-0 rounded-xl bg-stone-50 px-3 py-2.5">
									<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">
										Identidade bancária
									</p>
									<p className="mt-1 truncate text-xs font-medium text-stone-700">
										{supplier.bankAliases.at(0) ?? "Ainda não reconhecido no extrato"}
									</p>
								</div>
								<label className="text-xs font-semibold text-stone-700">
									Fornece
									<select
										value={supplier.category.id}
										disabled={supplierCategoryMutation.isPending}
										onChange={(event) =>
											supplierCategoryMutation.mutate({
												supplierId: supplier.id,
												categoryId: supplyCategoryIdSchema.assert(event.target.value),
											})
										}
										className="mt-1.5 h-10 w-full rounded-xl border border-stone-200 bg-white px-3 text-sm font-medium text-stone-700 outline-none transition focus:border-emerald-700 focus:ring-3 focus:ring-emerald-100 disabled:cursor-wait disabled:opacity-60"
									>
										{data.supplyCategories.map((category) => (
											<option key={category.id} value={category.id}>
												{category.name}
											</option>
										))}
									</select>
								</label>
							</div>
						</div>
					))}
				</div>
			</section>
		</>
	);
}
